import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation, useQueryClient } from 'react-query'
import { useLocation, useNavigate } from 'react-router-dom'

import {
  EMAIL_FIELD_NAME,
  PASSWORD_FIELD_NAME,
} from '@hapana/shared/web/components/shared/auth/login-form/login-form.constants'
import { getErrorMessage } from '@hapana/shared/web/services/gqty'
import { TOAST_PROPS_OPTIONS } from '@hapana/shared/web/components/shared/toast/toast.constants'
import { useToast } from '@hapana/shared/web/components/shared/toast/toast.hooks'
import useAuthStore from '../stores/auth.store'
import { EMAIL_FIELD_NAME as FORGOT_PASSWORD_EMAIL_FIELD_NAME } from '_pages/login/components/forgot-password/forgot-password.constants'
import {
  CODE_FIELD_NAME,
  NEW_PASSWORD_FIELD_NAME,
} from '_pages/login/components/reset-password/reset-password.constants'
import { NEW_PASSWORD_FIELD_NAME as TEMP_NEW_PASSWORD_FIELD_NAME } from '@hapana/shared/web/components/shared/auth/temp-password-form/temp-password-form.constants'
import {
  completePassword,
  confirmPasswordReset,
  requestPasswordReset,
  signOut,
  adminSignInAsUser,
  loginAsCoreSigOut,
} from '_services/auth.service'

import { ROUTES } from '../app.constants'

export const useSignIn = (options) => {
  const { openToast } = useToast()
  const { signIn } = useAuthStore()
  const { t } = useTranslation('admin')

  const onSuccess = async (data) => {
    if (options?.onSuccess) {
      options.onSuccess(data)
    } else {
      console.log('⚠️ No onSuccess callback provided in options')
    }
  }

  const onError = (error) => {
    if (options?.onError) {
      return options.onError(error)
    }
    openToast({
      title: getErrorMessage(error, t('auth.errors.invalidCredentials')),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
  }

  return useMutation(
    (data) => {
      return signIn(data[EMAIL_FIELD_NAME], data[PASSWORD_FIELD_NAME])
    },
    {
      onError,
      onSuccess,
    }
  )
}

export const useSignOut = (options) => {
  const { openToast } = useToast()
  const navigate = useNavigate()
  const { t } = useTranslation('admin')

  const onSuccess = async (data) => {
    // Note: React Query and GQty caches are cleared in auth.service.js signOut()
    options?.onSuccess(data)
    // Don't pass state.from on intentional logout — we don't want to redirect back
    // to the page the user was on (e.g. /profile) after they log back in.
    navigate(ROUTES.login.path, { replace: true })
  }

  const onError = (error) => {
    if (options?.onError) {
      options?.onError(error)
    } else {
      openToast({
        title: getErrorMessage(error, t('auth.errors.signOutFailed')),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      })
    }
  }

  return useMutation(() => signOut(), {
    onError,
    onSuccess,
  })
}

export const useCompletePassword = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('admin')

  const onSuccess = async (data) => {
    // Headers are now set automatically by useAuth hook
    options?.onSuccess(data)
  }

  const onError = (error) => {
    if (options?.onError) {
      options?.onError(error)
    } else {
      openToast({
        title: getErrorMessage(error, t('auth.errors.completePasswordFailed')),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      })
    }
  }

  return useMutation((data) => completePassword(data.user, data[TEMP_NEW_PASSWORD_FIELD_NAME]), {
    onError,
    onSuccess,
  })
}

export const useAuth = () => {
  const { user, idToken, loading } = useAuthStore()
  const hasValidSession = !!user && !!idToken
  const session = hasValidSession ? { idToken: { jwtToken: idToken } } : null

  return {
    isLoading: loading,
    isLoadingAuth: loading,
    session,
    hasValidSession,
    user,
    idToken,
  }
}

export const useRequestForgotPassword = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('admin')

  const onSuccess = async (data) => {
    options?.onSuccess(data)
    openToast({
      title: t('modals.forgotPassword.toastTitle'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })
  }

  const onError = (error) => {
    if (options?.onError) {
      options?.onError(error)
    } else {
      openToast({
        title: getErrorMessage(error, t('auth.errors.emailNotFound')),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      })
    }
  }

  return useMutation((data) => requestPasswordReset(data[FORGOT_PASSWORD_EMAIL_FIELD_NAME]), {
    onError,
    onSuccess,
  })
}

export const useConfirmPasswordReset = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('admin')

  const onSuccess = async (data) => {
    options?.onSuccess(data)
    openToast({
      title: t('modals.resetPassword.toastTitle'),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
    })
  }

  const onError = (error) => {
    options?.onError?.(error)
  }

  return useMutation(
    (data) => confirmPasswordReset(data[CODE_FIELD_NAME], data[NEW_PASSWORD_FIELD_NAME]),
    {
      onError,
      onSuccess,
    }
  )
}

export const useAdminSignIn = (options) => {
  const { openToast } = useToast()
  const { t } = useTranslation('admin')

  const onSuccess = async (data) => {
    options?.onSuccess?.(data)
  }

  const onError = (error) => {
    if (options?.onError) {
      return options.onError(error)
    }
    openToast({
      title: getErrorMessage(error, t('auth.errors.invalidCredentials')),
      severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
    })
  }

  return useMutation(adminSignInAsUser, {
    onError,
    onSuccess,
  })
}

export const useLoginAsCoreSigOut = (options) => {
  const { openToast } = useToast()
  const navigate = useNavigate()
  const { t } = useTranslation('admin')

  const onSuccess = async (data) => {
    // Note: loginAsCoreSigOut already calls signOut() which clears all storage and caches
    options?.onSuccess?.(data)
    navigate(ROUTES.login.path, { replace: true })
  }

  const onError = (error) => {
    if (options?.onError) {
      options?.onError(error)
    } else {
      openToast({
        title: getErrorMessage(error, t('auth.errors.signOutFailed')),
        severity: TOAST_PROPS_OPTIONS.SEVERITY.ERROR,
      })
    }
  }

  return useMutation(loginAsCoreSigOut, {
    onError,
    onSuccess,
  })
}

export const useLoginAsSync = () => {
  const { openToast } = useToast()
  const { t } = useTranslation('core')
  const queryClient = useQueryClient()

  useEffect(() => {
    const handleStorageChange = async (event) => {
      if (event.key === 'activeLoginAsSession' && event.newValue) {
        queryClient.invalidateQueries()

        openToast({
          title: t('login.sessionChanged'),
          severity: TOAST_PROPS_OPTIONS.SEVERITY.SUCCESS,
        })
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [openToast, t, queryClient])
}
